/**
 * Reichweitenmessung mit selbst betriebenem Matomo.
 *
 * Die Vorgabe war „DSGVO-konform", und daraus folgen ein paar harte
 * Entscheidungen, die hier an genau einer Stelle stehen:
 *
 * 1. **Keine Cookies, kein `localStorage` fuer die Messung.** `disableCookies`
 *    laeuft, bevor irgendetwas gesendet wird. Damit wird auf dem Geraet nichts
 *    gespeichert und nichts ausgelesen — der Tatbestand des § 25 TDDDG, der
 *    sonst eine Einwilligung verlangt, ist gar nicht erst eroeffnet. Genau
 *    deshalb gibt es hier auch kein Cookie-Banner: Es gaebe nichts, worin
 *    einzuwilligen waere.
 * 2. **„Do Not Track" wird befolgt.** Wer es im Browser gesetzt hat, wird
 *    nicht gezaehlt. Das ist rechtlich nicht zwingend, aber es waere
 *    unaufrichtig, ein Signal zu ignorieren, das genau das sagt, worum es
 *    hier geht.
 * 3. **Ein Abschalter im Spiel.** Er wird *vor* dem Laden ausgewertet: Wer
 *    widerspricht, laedt `matomo.js` gar nicht erst herunter. Das ist
 *    wirksamer als Matomos eigener Opt-out, der einen Cookie setzen wuerde —
 *    also ausgerechnet das, was wir vermeiden. Denselben Schalter bietet
 *    `public/impressum.html` in der Datenschutzerklaerung an, damit der
 *    Widerspruch dort erklaert werden kann, wo er beschrieben wird.
 * 4. **Kein Skript ohne Not.** Das Modul laedt nichts, solange keine
 *    Messstelle eingetragen ist.
 *
 * Was *nicht* hier entschieden werden kann, weil es auf dem Server liegt:
 * die IP-Anonymisierung, die Aufbewahrungsdauer und das Abschalten der
 * Besuchsprofile. In der Matomo-Instanz ist dafuer der Modus „Einhaltung der
 * CNIL-Vorgaben erzwingen" aktiv (Administration → Privatsphaere). Er sperrt:
 * IP-Maskierung um zwei Bytes, Besuchsprotokoll und Besucherprofile aus, kein
 * Cross-Domain-Tracking, Kampagnen-/Werbekennungen werden verworfen, Rohdaten
 * nach 180 Tagen geloescht. Genau das steht in `public/impressum.html` — wer
 * den Modus dort abschaltet, macht die Erklaerung falsch.
 */

/** Die selbst betriebene Matomo-Instanz. Leer lassen heisst: keine Messung. */
const MATOMO_URL: string = 'https://hoerspielme.de/';
const MATOMO_SITE_ID = '5';

/**
 * Exportiert, weil `public/impressum.html` denselben Schluessel liest und
 * schreibt — die Datenschutzerklaerung bietet den Widerspruch auch ohne das
 * laufende Spiel an. Ein Test haelt beide Seiten zusammen.
 */
export const OPT_OUT_KEY = 'bermuda.analytics.optout.v1';
export const OPT_OUT_VALUE = '1';

declare global {
  interface Window {
    _paq?: unknown[][];
  }
}

function storage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/**
 * Hat die Person der Messung widersprochen?
 *
 * Der Widerspruch selbst liegt im `localStorage` — das ist zulaessig und sogar
 * geboten: Eine Entscheidung *gegen* Verarbeitung zu speichern, ist nach
 * § 25 Abs. 2 TDDDG unbedingt erforderlich, um sie ueberhaupt beachten zu
 * koennen. Andernfalls muesste man bei jedem Aufruf erneut widersprechen.
 */
export function analyticsOptedOut(): boolean {
  return storage()?.getItem(OPT_OUT_KEY) === OPT_OUT_VALUE;
}

export function setAnalyticsOptOut(optOut: boolean): void {
  try {
    if (optOut) storage()?.setItem(OPT_OUT_KEY, OPT_OUT_VALUE);
    else storage()?.removeItem(OPT_OUT_KEY);
  } catch {
    // Ohne Speicher greift der Widerspruch nur fuer diesen Aufruf. Das ist
    // immer noch besser, als ihn zu verwerfen.
  }
}

/** Sendet der Browser „Do Not Track"? */
function doNotTrack(): boolean {
  const nav = window.navigator as Navigator & { msDoNotTrack?: string };
  const value =
    nav.doNotTrack ?? (window as Window & { doNotTrack?: string }).doNotTrack ?? nav.msDoNotTrack;
  return value === '1' || value === 'yes';
}

/**
 * Warum gerade nicht gemessen wird — oder `null`, wenn gemessen wird.
 *
 * Oeffentlich, damit die Einstellungen den Grund nennen koennen, statt nur
 * einen toten Schalter zu zeigen.
 */
export function analyticsBlockedBecause(): 'unconfigured' | 'optout' | 'dnt' | null {
  if (MATOMO_URL === '') return 'unconfigured';
  if (analyticsOptedOut()) return 'optout';
  if (doNotTrack()) return 'dnt';
  return null;
}

let started = false;

/**
 * Startet die Messung, falls sie erlaubt ist.
 *
 * Einmal pro Seitenaufruf. Ein spaeteres Ein- oder Ausschalten in den
 * Einstellungen wirkt erst beim naechsten Laden — ein einmal geladenes
 * Fremdskript liesse sich ohnehin nicht zuverlaessig wieder entfernen, und so
 * zu tun, als ginge das, waere die unehrlichere Loesung.
 */
export function startAnalytics(): void {
  if (started) return;
  if (analyticsBlockedBecause() !== null) return;
  started = true;

  const paq = (window._paq = window._paq ?? []);

  // Reihenfolge zaehlt: Alles, was die Messung einschraenkt, muss vor dem
  // ersten Seitenaufruf stehen.
  paq.push(['disableCookies']);
  paq.push(['setDoNotTrack', true]);
  paq.push(['setTrackerUrl', `${MATOMO_URL}matomo.php`]);
  paq.push(['setSiteId', MATOMO_SITE_ID]);
  paq.push(['trackPageView']);
  paq.push(['enableLinkTracking']);

  const script = document.createElement('script');
  script.async = true;
  script.src = `${MATOMO_URL}matomo.js`;
  document.head.appendChild(script);
}
